import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { useAuthStore } from "../store/authStore";
import { ApiTokensSection } from "../components/ApiTokensSection";
import { Button } from "../components/Button";
import { ExportModal } from "../components/ExportModal";

export function SettingsPage() {
  const navigate = useNavigate();
  const clerk = useClerk();
  const { user } = useAuthStore();
  const [userIdCopied, setUserIdCopied] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const handleCopyUserId = async () => {
    if (!user?.id) return;
    try {
      await navigator.clipboard.writeText(user.id);
      setUserIdCopied(true);
      setTimeout(() => setUserIdCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy user ID:", err);
    }
  };

  const handleSignOut = async () => {
    await clerk.signOut();
  };

  return (
    <div className="min-h-screen bg-page">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/")}
              variant="icon"
              icon="arrow_back"
              title="Back to trips"
              aria-label="Back to trips"
            />
            <h1 className="text-lg font-bold text-primary tracking-tight">
              Settings
            </h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Account Section */}
          <section className="bg-white rounded-2xl p-6 border border-border">
            <div className="flex items-start gap-3 mb-4">
              <span className="material-symbols-outlined text-2xl text-accent">
                account_circle
              </span>
              <div className="flex-1">
                <h2 className="text-base font-bold text-primary mb-1">
                  Account
                </h2>
                <p className="text-sm text-secondary">
                  Manage your account and authentication.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* User Info */}
              {user && (
                <div className="space-y-3">
                  {user.email && (
                    <div>
                      <span className="text-xs font-medium text-tertiary uppercase tracking-wide">
                        Email
                      </span>
                      <p className="text-sm text-primary mt-0.5">
                        {user.email}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-xs font-medium text-tertiary uppercase tracking-wide">
                      User ID
                    </span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className="text-xs text-secondary bg-subtle px-2 py-1 rounded font-mono">
                        {user.id}
                      </code>
                      <Button
                        onClick={handleCopyUserId}
                        variant="icon"
                        icon={userIdCopied ? "check" : "content_copy"}
                        title="Copy User ID"
                        aria-label="Copy User ID"
                      />
                      {userIdCopied && (
                        <span className="text-xs text-success">Copied!</span>
                      )}
                    </div>
                    <p className="text-xs text-tertiary mt-1">
                      Used for PDF import and API integrations.
                    </p>
                  </div>
                </div>
              )}

              {/* Sign Out */}
              <Button onClick={handleSignOut} variant="danger" icon="logout">
                Sign Out
              </Button>
            </div>
          </section>

          {/* API Tokens Section */}
          <ApiTokensSection />

          {/* Data Export Section */}
          <section className="bg-white rounded-2xl p-6 border border-border">
            <div className="flex items-start gap-3 mb-4">
              <span className="material-symbols-outlined text-2xl text-accent">
                download
              </span>
              <div className="flex-1">
                <h2 className="text-base font-bold text-primary mb-1">
                  Data Export
                </h2>
                <p className="text-sm text-secondary">
                  Download your data as JSON for backup or to use with other
                  tools.
                </p>
              </div>
            </div>

            <Button
              onClick={() => setShowExportModal(true)}
              data-testid="export-data-button"
              variant="primary"
              icon="download"
            >
              Export Data
            </Button>
          </section>

          {/* About Section */}
          <section className="bg-white rounded-2xl p-6 border border-border">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-2xl text-accent">
                info
              </span>
              <div className="flex-1">
                <h2 className="text-base font-bold text-primary mb-1">About</h2>
                <p className="text-sm text-secondary">
                  PlotPlanGo v{__APP_VERSION__}
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
  );
}
